import { TimePeriodSwitcher } from "./components/timePeriodSwitcher";
import { TimePeriod } from "./historicalChart.consants";
import { memo, useMemo, useState } from "react";
import { SideSelector } from "./components/sideSelector";
import { PercentileToggle } from "./components/percentileToggle";
import { AtsIndicator, Side } from "@/app/data/api";
import { Chart } from "./components/chart/chart";
import { DateTime } from "luxon";
import { getHistoricalChartStatus } from "./historicalChart.utils";
import { Bond } from "@/app/data/dataProvider";
import { PriceType } from "@/types/types";
import { useHistoricalChartInferences } from "./hooks/useHistoricalChartInferences";
import { LoadingOverlay } from "@/app/loading";

export const HistoricalChart = memo(({
  bond,
  atsIndicator,
  quantity,
  priceType,
}: {
  bond: Bond;
  atsIndicator: AtsIndicator,
  quantity: number;
  priceType: PriceType
}) => {
  const [customPeriod, setCustomPeriod] = useState<[number, number] | undefined>(undefined);
  const [timePeriod, setTimePeriod] = useState(TimePeriod["1D"]);
  const [percentileActive, setPercentileActive] = useState(true);
  const [sides, setSides] = useState<Record<Side, boolean>>({
    [Side.bid]: true,
    [Side.dealer]: true,
    [Side.offer]: true,
  })

  const settlementDate = useMemo(() => {
    return DateTime.fromISO(bond.settlementDate).toJSDate();
  }, [bond.settlementDate]);

  // get inferences for current time period
  const {
    times,
    bidInferences,
    dealerInferences,
    offerInferences,
    finraHolidays,
  } = useHistoricalChartInferences({
    figi: bond.figi,
    atsIndicator,
    quantity,
    priceType,
    bond,
    timePeriod,
    sides,
    settlementDate,
    customPeriod,
  });

  // a line needs >= 2 points, so <2 in-range inferences resolves to an empty state rather than an
  // endless loader (a fully-closed window or a sparse/single-point bond). While the FINRA calendar is
  // still loading (finraHolidays null), times is [] -- so pass it through to show the loader, not "no data".
  const status = getHistoricalChartStatus(times, bidInferences, dealerInferences, offerInferences, finraHolidays);

  return (
    <>
      <div className='pt-1 min-[1100px]:pt-[1.625rem] min-[1100px]:h-[3.625rem] relative flex min-[1250px]:ml-[5rem] min-[1100px]:gap-[0.25rem] min-[1250px]:gap-[2rem] flex-col min-[1100px]:flex-row'>
        <div className="w-fit min-[1100px]:self-center">
          <SideSelector sides={sides} setSides={setSides} />
        </div>
        <div className="w-fit min-[1100px]:self-center">
          <PercentileToggle active={percentileActive} setActive={setPercentileActive} />
        </div>
        <TimePeriodSwitcher 
          timePeriod={timePeriod} 
          setTimePeriod={setTimePeriod} 
          bond={bond} 
          onCustomPeriodApply={(startDate, endDate) => {
            // convert start date to start of the day using luxon and end date to end of the day 
            // const _startDate = DateTime.fromMillis(startDate, ).startOf('day').toMillis();
            // const _endDate = DateTime.fromMillis(endDate).endOf('day').toMillis();
            setCustomPeriod([startDate, endDate]);
          }}
        />
      </div>

      <div className="min-h-[0px] flex-1 relative">
        {status === 'loading' && (
          <LoadingOverlay />
        )}
        {status === 'no-data' && (
          <div className="absolute inset-0 flex items-center justify-center text-[#5D5F9D]">
            No data for this period
          </div>
        )}
        {status === 'ready' && (
          <Chart
            timePeriod={timePeriod}
            sides={sides}
            percentileActive={percentileActive}
            bidInferences={bidInferences}
            offerInferences={offerInferences}
            dealerInferences={dealerInferences}
            priceType={priceType}
            times={times}
          />
        )}
      </div>
    </>
  );
})

